import React from 'react';

import Link from '@atlaskit/link';
import { Box, Stack } from '@atlaskit/primitives/compiled';

import { createLinkClickHandlers } from './create-link-click-handlers';
import type { OnLinkClick } from './types';

interface LinkListErroredFallbackProps {
	onLinkClick?: OnLinkClick;
	/**
	 * Prefix used to namespace the fallback item testIds
	 */
	testIdPrefix: string;
	urls: string[];
}

/**
 * Fallback rendered when every URL passed into a link list variant has errored
 * out via the Smart Link Card. Rather than attempting another smart-card
 * resolution (which would also error and throw in the same environment), we
 * render a plain ADS `Link` for each URL so the user still has something
 * clickable.
 *
 * The wrapper `Box` carries `onAuxClick` / `onContextMenu` so middle-click and
 * right-click telemetry behavior matches the resolved (block) variant.
 */
export const LinkListErroredFallback = ({
	urls,
	onLinkClick,
	testIdPrefix,
}: LinkListErroredFallbackProps): React.JSX.Element => {
	return (
		<Stack space="space.100">
			{urls.map((url, idx) => {
				const { onClick, onAuxClick, onContextMenu } = createLinkClickHandlers(
					onLinkClick,
					url,
					idx,
				);
				return (
					<Box
						key={`${url}-${idx}-errored-fallback`}
						testId={`${testIdPrefix}-errored-fallback-item-${idx}`}
						onAuxClick={onAuxClick}
						onContextMenu={onContextMenu}
					>
						<Link href={url} onClick={onClick} target="_blank" rel="noopener noreferrer">
							{url}
						</Link>
					</Box>
				);
			})}
		</Stack>
	);
};
